import { useCallback, useEffect, useState } from 'react'
import { TourStepDefinition } from './steps'

const WAIT_MS = 1000
const POLL_MS = 100

// Tabs stay mounted behind `display: none`; a closed drawer is `visibility: hidden` and off screen.
const isShown = (element: HTMLElement): boolean => {
  if (getComputedStyle(element).visibility === 'hidden') {
    return false
  }
  for (let node: HTMLElement | null = element; node; node = node.parentElement) {
    if (getComputedStyle(node).display === 'none') {
      return false
    }
  }

  return true
}

const findTarget = ({ selector, pickLast }: TourStepDefinition): HTMLElement | null => {
  if (!selector) {
    return null
  }
  const matches = Array.from(document.querySelectorAll<HTMLElement>(selector))

  return (pickLast ? matches.reverse() : matches).find(isShown) ?? null
}

const reveal = (element: HTMLElement) => {
  const { top, bottom } = element.getBoundingClientRect()
  if (top < 0 || bottom > window.innerHeight) {
    element.scrollIntoView({ block: 'nearest' })
  }
}

type Found = { step: TourStepDefinition; element: HTMLElement | null }

export const useTourTarget = (step: TourStepDefinition, openTab: (tabId: string) => void) => {
  const [found, setFound] = useState<Found | null>(null)
  const [search, setSearch] = useState(0)
  const relocate = useCallback(() => {
    setFound(null)
    setSearch((count) => count + 1)
  }, [])

  useEffect(() => {
    if (step.tab) {
      openTab(step.tab)
    }
    if (!step.selector) {
      setFound({ step, element: null })
      return undefined
    }

    const startedAt = Date.now()
    const check = () => {
      const element = findTarget(step)
      if (!element && Date.now() - startedAt < WAIT_MS) {
        return false
      }
      if (element) {
        reveal(element)
      }
      setFound({ step, element })

      return true
    }
    if (check()) {
      return undefined
    }
    const timer = window.setInterval(() => check() && window.clearInterval(timer), POLL_MS)

    return () => window.clearInterval(timer)
  }, [step, openTab, search])

  const current = found?.step === step ? found : null

  return {
    ready: current !== null,
    element: current?.element ?? null,
    missing: current !== null && current.element === null && Boolean(step.selector),
    relocate,
  }
}

const sameRect = (a: DOMRect | null, b: DOMRect) =>
  a !== null && a.top === b.top && a.left === b.left && a.width === b.width && a.height === b.height

// Panels shift as their data loads, and a re-render can replace the target outright.
export const useTrackedRect = (element: HTMLElement | null, onLost: () => void): DOMRect | null => {
  const [rect, setRect] = useState<DOMRect | null>(null)

  useEffect(() => {
    if (!element) {
      setRect(null)
      return undefined
    }
    let frame = 0
    const track = () => {
      if (!element.isConnected) {
        onLost()
        return
      }
      const next = element.getBoundingClientRect()
      setRect((previous) => (sameRect(previous, next) ? previous : next))
      frame = requestAnimationFrame(track)
    }
    track()

    return () => cancelAnimationFrame(frame)
  }, [element, onLost])

  return element ? rect : null
}
